// src/pages/Reports/index.js

import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  Assessment as ReportsIcon,
  TrendingUp as TrendingIcon,
  AttachMoney as RevenueIcon,
  People as ClientsIcon,
  CalendarMonth as AppointmentsIcon,
  Add as AddIcon,
  Download as DownloadIcon,
  Share as ShareIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Schedule as ScheduleIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  OpenInNew as OpenInNewIcon,
  Dashboard as DashboardIcon
} from '@mui/icons-material';
import DynamicMetabaseDashboard from '../../components/DynamicMetabaseDashboard';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { formatCurrency as formatCurrencyUtil } from '../../utils/currency';
import DevOnly from '../../components/DevOnly';
import { getCurrentUserInfo } from '../../utils/auth';
import apiClient from '../../api/client';

// Tab Panel Component
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`reports-tabpanel-${index}`}
      aria-labelledby={`reports-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const ReportsAnalytics = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0); // Default to "By Category" tab
  const [dateRange, setDateRange] = useState('last_30_days');
  const [selectedDashboard, setSelectedDashboard] = useState('executive');
  
  // Get clinic info from JWT token using auth utility
  const userInfo = getCurrentUserInfo();
  
  // For KSL, the schema_name is 'ksl_clinic_5' (no 'clinic_' prefix)
  // Check multiple possible locations for the schema name
  const clinicSchemaName = userInfo?.schemaName || 
                          userInfo?.schema_name || 
                          userInfo?.clinic_schema ||
                          localStorage.getItem('clinicSchema') ||
                          'ksl_clinic_5'; // Default to KSL for now
                          
  const clinicName = userInfo?.clinicName || 
                    userInfo?.clinic_name || 
                    userInfo?.clinic || 
                    'KSL Clinic';

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Fetch reports stats
  const { data: reportsStats, isLoading: statsLoading } = useQuery({
    queryKey: ['reports-stats'],
    queryFn: async () => {
      try {
        const response = await apiClient.get('/reports/stats');
        return response.data;
      } catch (error) {
        console.error('Error fetching reports stats:', error);
        return {
          available: 0,
          recentlyGenerated: 0,
          lastGenerated: 'Never',
          total: 0
        };
      }
    },
    staleTime: 5 * 60 * 1000,
  });

  // Fetch all reports
  const { data: allReports = [], isLoading: reportsLoading } = useQuery({
    queryKey: ['all-reports'],
    queryFn: async () => {
      try {
        const response = await apiClient.get('/reports/');
        console.log('Reports API response:', response.data);
        return response.data || [];
      } catch (error) {
        console.error('Error fetching reports:', error);
        return [];
      }
    },
    staleTime: 5 * 60 * 1000,
  });

  // Group reports by category
  const reportsByCategory = allReports.reduce((acc, report) => {
    const category = report.data_category || 'general';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(report);
    return acc;
  }, {});

  // Get favorite reports
  const favoriteReports = allReports.filter(report => report.is_favourite);

  const tabs = [
    { label: 'By Category', icon: <TrendingIcon />, id: 'category' },
    { label: 'Analytics Dashboards', icon: <DashboardIcon />, id: 'dashboards', devOnly: true }
  ];

  const formatCurrency = (amount) => formatCurrencyUtil(amount);

  const formatDate = (dateString) => {
    if (!dateString) return 'No date';
    return new Date(dateString).toLocaleDateString();
  };

  const stats = reportsStats || {
    available: 0,
    recentlyGenerated: 0,
    lastGenerated: 'Never',
    total: 0
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Reports & Analytics
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Generate insights, track performance, and analyze your clinic's data
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Date Range</InputLabel>
            <Select
              value={dateRange}
              label="Date Range"
              onChange={(e) => setDateRange(e.target.value)}
            >
              <MenuItem value="last_7_days">Last 7 Days</MenuItem>
              <MenuItem value="last_30_days">Last 30 Days</MenuItem>
              <MenuItem value="last_3_months">Last 3 Months</MenuItem>
              <MenuItem value="last_6_months">Last 6 Months</MenuItem>
              <MenuItem value="last_year">Last Year</MenuItem>
            </Select>
          </FormControl>
          <DevOnly>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/reports/builder')}
              size="large"
            >
              Create Report
            </Button>
          </DevOnly>
        </Box>
      </Box>

      {/* Quick Stats */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={4}>
          <MetricCard
            title="Available Reports"
            value={stats.available}
            icon={<ReportsIcon />}
            color="primary"
            isLoading={statsLoading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <MetricCard
            title="Report Categories"
            value={Object.keys(reportsByCategory).length}
            icon={<TrendingIcon />}
            color="info"
            isLoading={reportsLoading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <MetricCard
            title="Last Generated"
            value={stats.lastGenerated}
            icon={<ScheduleIcon />}
            color="warning"
            isLoading={statsLoading}
          />
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
        >
          {tabs.map((tab, index) => {
            if (tab.devOnly) {
              return (
                <DevOnly key={index}>
                  <Tab
                    label={tab.label}
                    icon={tab.icon}
                    iconPosition="start"
                    id={`reports-tab-${index}`}
                    aria-controls={`reports-tabpanel-${index}`}
                  />
                </DevOnly>
              );
            }
            return (
              <Tab
                key={index}
                label={tab.label}
                icon={tab.icon}
                iconPosition="start"
                id={`reports-tab-${index}`}
                aria-controls={`reports-tabpanel-${index}`}
              />
            );
          })}
        </Tabs>

        {/* By Category Tab */}
        <TabPanel value={activeTab} index={0}>
          <CategoryReportsTab
            reportsByCategory={reportsByCategory}
            isLoading={reportsLoading}
            navigate={navigate}
            formatDate={formatDate}
          />
        </TabPanel>

        {/* Analytics Dashboards Tab - Dev Only */}
        <TabPanel value={activeTab} index={1}>
          <DevOnly>
            <Box>
              {/* Dashboard Selector */}
              <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
                <FormControl sx={{ minWidth: 250 }}>
                  <InputLabel>Select Dashboard</InputLabel>
                  <Select
                    value={selectedDashboard}
                    onChange={(e) => setSelectedDashboard(e.target.value)}
                    label="Select Dashboard"
                  >
                    <MenuItem value="executive">Executive Overview</MenuItem>
                    <MenuItem value="clients">Client Analytics</MenuItem>
                    <MenuItem value="appointments">Appointment Analytics</MenuItem>
                    <MenuItem value="services">Service Performance</MenuItem>
                    <MenuItem value="financial">Financial Insights</MenuItem>
                  </Select>
                </FormControl>

                {clinicSchemaName && (
                  <Chip
                    label={`Schema: ${clinicSchemaName}`}
                    color="primary"
                    variant="outlined"
                    size="small"
                  />
                )}
              </Box>

              {/* Dashboard Display */}
              <DynamicMetabaseDashboard
                dashboardType={selectedDashboard}
                clinicSchemaName={clinicSchemaName}
                clinicName={clinicName}
                height="700px"
              />

              {/* Dashboard Info */}
              <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                <Typography variant="h6" gutterBottom>
                  About This Dashboard
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  {selectedDashboard === 'executive' &&
                    `High-level KPIs for ${clinicName} including total clients, monthly revenue, appointments, and service utilization trends.`}
                  {selectedDashboard === 'clients' &&
                    'Client demographics, acquisition trends, and referral sources analysis.'}
                  {selectedDashboard === 'appointments' &&
                    'Daily appointment volumes, status breakdowns, and busiest days/hours for optimal scheduling.'}
                  {selectedDashboard === 'services' &&
                    'Top performing services, revenue by category, and service duration analysis for optimization.'}
                  {selectedDashboard === 'financial' &&
                    'Revenue trends, payment method distribution, and outstanding invoice tracking.'}
                </Typography>

                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<OpenInNewIcon />}
                  onClick={() => window.open('http://localhost:3001', '_blank')}
                  sx={{ mt: 1 }}
                >
                  Open Metabase in New Tab
                </Button>
              </Box>
            </Box>
          </DevOnly>
        </TabPanel>
      </Paper>
    </Box>
  );
};

// Category Reports Tab Component
const CategoryReportsTab = ({ reportsByCategory, isLoading, navigate, formatDate }) => {
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  const categories = Object.keys(reportsByCategory).sort();

  if (categories.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">
          No reports available. Sync reports from Pabau to see them here.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      {categories.map((category) => (
        <Accordion key={category}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
              <Chip
                label={category}
                color={getCategoryColor(category)}
                sx={{ textTransform: 'capitalize' }}
              />
              <Typography variant="h6" sx={{ textTransform: 'capitalize' }}>
                {category} Reports
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto', mr: 2 }}>
                {reportsByCategory[category].length} reports
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Report Name</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Last Updated</TableCell>
                    <TableCell align="center">Favorite</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reportsByCategory[category].map((report) => (
                    <TableRow key={report.id} hover>
                      <TableCell>
                        <Typography variant="body2">{report.report_name}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {report.description || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>{formatDate(report.updated_date || report.created_date)}</TableCell>
                      <TableCell align="center">
                        {report.is_favourite ? (
                          <StarIcon color="primary" fontSize="small" />
                        ) : (
                          <StarBorderIcon color="disabled" fontSize="small" />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color="text.secondary" fontSize="small">
                          View in Pabau
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
};

// Metric Card Component
const MetricCard = ({ title, value, icon, color, isLoading }) => {
  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography color="text.secondary" variant="caption">
              {title}
            </Typography>
            <Typography variant="h5" sx={{ mt: 0.5 }}>
              {isLoading ? <CircularProgress size={20} /> : value}
            </Typography>
          </Box>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            width: 48,
            height: 48,
            borderRadius: '50%',
            backgroundColor: (theme) => theme.palette[color].light + '30',
            color: (theme) => theme.palette[color].main
          }}>
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

// Helper function to get category color
const getCategoryColor = (category) => {
  const colors = {
    activities: 'primary',
    appointment: 'success',
    client: 'info',
    lead: 'warning',
    line_item: 'error',
    payment: 'secondary',
    product: 'primary',
    service: 'success',
    team: 'info',
    general: 'default'
  };
  return colors[category?.toLowerCase()] || 'default';
};

export default ReportsAnalytics;